import axios from 'axios'

import { authHeader } from './authHeader'

import { getRuntimeConfig } from '@/core/config/runtimeConfig'

// No fijamos baseURL en tiempo de módulo: el interceptor lo resuelve en cada request
// leyendo el singleton hidratado por ConfigRuntimeProvider (/api/config).
// Esto permite que el mismo bundle sirva a EC/PE y a QA/UAT/PROD sin rebuild.
// Exportado para poder probar los interceptores; el resto de la app usa las funciones de abajo.
export const axiosApi = axios.create()

// Marca de "este request ya se reintentó": la pone el interceptor de respuesta ante un 401
// y la lee el de request para pedir un token fresco. Garantiza UN solo reintento.
// `_reintentosServidor` lleva aparte la cuenta de los reintentos por caída momentánea del backend.
interface IRequestReintentable {
  _reintentado?: boolean
  _reintentosServidor?: number
}

// Códigos en los que el backend no está: reiniciando por un despliegue, o detrás de un balanceador
// que todavía no lo ve arriba. No son un error de la petición, así que vale repetirla.
const CODIGOS_BACKEND_CAIDO = [502, 503, 504]

// Solo se repiten los métodos que no cambian nada: repetir un POST duplicaría el efecto.
const METODOS_SEGUROS = ['get', 'head']

// Dos reintentos, en el orden en que se esperan. El App Service tarda decenas de segundos en
// volver, así que esto no salva un reinicio largo —ahí el usuario ve el error, que es lo honesto—
// pero sí tapa el hueco de los primeros segundos, que es donde caen casi todos los 503.
const ESPERAS_MS = [1000, 3000]

const esperar = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

// Set the AUTH token and BASE URL for any request
axiosApi.interceptors.request.use(
  async (config) => {
    // Resolver baseURL en runtime
    config.baseURL = getRuntimeConfig().BASE_URL

    // Fetch the access token using your authHeader function
    const { accessToken } = await authHeader((config as IRequestReintentable)._reintentado === true)

    // Ensure the headers object exists and add the Authorization header
    config.headers = config.headers || {}
    config.headers['Authorization'] = `Bearer ${accessToken}`
    config.headers['Content-Type'] =
      config.headers['Content-Type'] ?? 'application/json'

    return config
  },
  (error) => {
    // Handle errors before the request is sent
    return Promise.reject(error)
  },
)

// 401 = la API rechazó el token (rotado, desfase de reloj). UN reintento con token fresco.
// 403 NO se reintenta ni cierra sesión: es falta de app role en Entra, reloguear no lo arregla.
// 502/503/504 = el backend se está reiniciando (cada despliegue lo hace, y no hay slots): se
// repite la lectura un par de veces antes de molestar al usuario. Ver CODIGOS_BACKEND_CAIDO.
axiosApi.interceptors.response.use(undefined, async (error) => {
  const config = error?.config as (typeof error.config & IRequestReintentable) | undefined
  const estado = error?.response?.status

  if (!config) {
    return Promise.reject(error)
  }

  if (estado === 401 && !config._reintentado) {
    config._reintentado = true

    return axiosApi(config)
  }

  const reintentables =
    CODIGOS_BACKEND_CAIDO.includes(estado) &&
    METODOS_SEGUROS.includes((config.method ?? 'get').toLowerCase())

  if (reintentables) {
    const hechos = config._reintentosServidor ?? 0

    if (hechos < ESPERAS_MS.length) {
      config._reintentosServidor = hechos + 1

      await esperar(ESPERAS_MS[hechos])

      return axiosApi(config)
    }
  }

  return Promise.reject(error)
})

// Las funciones de abajo son genéricas en lo que devuelven. El parámetro T lleva `= any` como
// valor por defecto a propósito: este archivo lo importan unos 120 archivos y ninguno se rompe,
// porque sin argumento el comportamiento es exactamente el de antes.
//
// La gracia es que ahora cada llamada puede declarar qué recibe, y a partir de ahí TypeScript
// deja de repartir `any` hacia el estado, las props y las interfaces:
//
//   const { value } = await get<RespuestaODataLista<Proveedor>>('Proveedores?$top=50')
//
// Los `any` que quedan aquí son esos valores por defecto y son deuda a propósito: desaparecen
// cuando todas las llamadas declaren su tipo, y hasta entonces el aviso del linter es señal
// legítima de que la migración sigue en curso.

export async function get<T = any>(url: string): Promise<T> {
  return axiosApi.get<T>(url).then((response) => response.data)
}

export async function postCustom<T = any, D = any>(url: string, data: D): Promise<T> {
  return axiosApi.post<T>(url, data).then((response) => response.data)
}

export async function post<T = any>(url: string, data: object): Promise<T> {
  return axiosApi.post<T>(url, { ...data }).then((response) => response.data)
}

export async function postJSON<T = any>(url: string, data: object): Promise<T> {
  const headers = { 'Content-Type': 'application/json' }

  return axiosApi.post<T>(url, data, { headers }).then((response) => response.data)
}

export async function postUpload<T = any, D = any>(url: string, data: D): Promise<T> {
  const headers = { 'Content-Type': 'multipart/form-data' }

  // Propaga el error (como el resto de helpers): cada caller lo maneja. Antes lo tragaba
  // y devolvía undefined, así que en un fallo el caller corría su rama de éxito (falso éxito + doble toast)
  return axiosApi.post<T>(url, data, { headers }).then((response) => response.data)
}

export async function postSalidas<T = any>(url: string): Promise<T> {
  return axiosApi.post<T>(url).then((response) => response.data)
}

export async function put<T = any>(url: string, data: object): Promise<T> {
  return axiosApi.put<T>(url, { ...data }).then((response) => response.data)
}

export async function putCustom<T = any, D = any>(url: string, data: D): Promise<T> {
  return axiosApi.put<T>(url, data).then((response) => response.data)
}

/**
 * PATCH: actualiza SOLO los campos que van en `data`.
 *
 * `put` reemplaza la fila entera —es lo que PUT significa— y del otro lado el controlador marca la
 * entidad completa como `EntityState.Modified`, asi que reescribe todas sus columnas con lo que el
 * cliente tenga cargado. Para editar un campo suelto eso pisa en silencio lo que otro haya cambiado
 * mientras tanto, incluso en columnas que la pantalla no muestra.
 */
export async function patch<T = any>(url: string, data: object): Promise<T> {
  return axiosApi.patch<T>(url, { ...data }).then((response) => response.data)
}

export async function del<T = any>(url: string, data?: object): Promise<T> {
  return axiosApi.delete<T>(url, data !== undefined ? { data } : undefined).then((response) => response.data)
}

export async function putUpload<T = any, D = any>(url: string, data: D): Promise<T> {
  const headers = { 'Content-Type': 'multipart/form-data' }

  // Propaga el error (como el resto de helpers): cada caller lo maneja. Antes lo tragaba
  // y devolvía undefined, así que en un fallo el caller corría su rama de éxito (falso éxito + doble toast)
  return axiosApi.put<T>(url, data, { headers }).then((response) => response.data)
}
